import { all, put, select, takeEvery } from 'redux-saga/effects';
import * as Actions from './actions';
import constants from '../../../../helpers/constant';
import { showToast } from '../notification';
import { get, findIndex, cloneDeep, find } from 'lodash';

import {
  getAllOrgsApi,
  fetchOrgByIdApi,
  fetchOrgGroupApi,
  createOrgGroupApi,
  updateOrgGroupApi,
  deleteOrgGroupApi,
  loginOrgGroupApi,
  rollbackOrgApi,
  fetchAccessibleOrgsApi,
  fetchOrgWhitelistByOrgId,
  fetchAttributionEngines
} from '../../../../helpers/orgManagementApi';

const FETCH_SUCCESS = 'SUCCESS';

function* loadAllOrgs() {
  const config = yield select( state =>  state.config);
  const fetchResults = yield getAllOrgsApi(config.urlApi);
  if (fetchResults && !fetchResults.error) {
    const clonedData = cloneDeep(fetchResults.data);
    const orgs = clonedData && clonedData.reduce((accum, currentOrg) => {
      const { status, id } = currentOrg;
      if (status == Actions.Status.ACTIVE) {
        currentOrg.profileImage = currentOrg.imageUrl;
        currentOrg.id = parseInt(id);
        accum.push(currentOrg);
      }
      return accum;
    }, []);
    yield put(Actions.setOrgs(orgs));
  } else {
    const errorToast = {
      message: 'Load orgs failed',
      action: 'Dismiss',
      type: constants.FAILED
    };
    yield put(Actions.setLoadOrgsError());
    yield put(showToast(errorToast));
  }
}

function* loadAccessibleOrgs() {
  const config = yield select( state =>  state.config);
  const fetchResults = yield fetchAccessibleOrgsApi(config.urlApi);
  if (fetchResults && !fetchResults.error) {

    // We don't need to set the currentOrg. These orgs only contain name
    // and id and are only to be used for display only purposes.
    const orgs = cloneDeep(fetchResults.data);
    yield put(Actions.setAccessibleOrgs(orgs));
  } else {
    const errorToast = {
      message: 'Load Accessible Orgs failed',
      action: 'Dismiss',
      type: constants.FAILED
    };
    yield put(Actions.setLoadAccessibleOrgsError());
    yield put(showToast(errorToast));
  }
}

function* loadOrgsByIds(action) {
  const { ids } = action.payload
  const config = yield select( state =>  state.config);
  const state = yield select(state => state[Actions.namespace]);
  const orgLoadingStatus = get(state, 'orgs.status');
  if (orgLoadingStatus !== Actions.Status.LOADED) {
    const promises = ids && ids.map(id => fetchOrgByIdApi(config.urlApi, id))
    const fetchResults = yield Promise.all(promises).then(result => {
      return { data: result };
    }).catch(error => { 
      return { error };
    });
  
    if (fetchResults && !fetchResults.error) {
      const clonedData = cloneDeep(fetchResults.data);
      const allOrgs = [...get(state, 'orgs.data', [])];
      const orgs = clonedData && clonedData.reduce((accum, currentOrg) => {
        const { id, status } = currentOrg;
        const orgIndex = findIndex(allOrgs, { id: parseInt(id) });
        if (status == Actions.Status.ACTIVE && orgIndex < 0) {
          currentOrg.profileImage = currentOrg.imageUrl;
          currentOrg.id = parseInt(id);
          accum.push(currentOrg);
        }
        return accum;
      }, []);
      yield put(Actions.setPartialOrgs(orgs));
    } else {
      const errorToast = {
        message: 'Load orgs failed',
        action: 'Dismiss',
        type: constants.FAILED
      };
      yield put(Actions.setLoadOrgsError());
      yield put(showToast(errorToast));
    }
  }
}

function* loadOrgGroups(action) {
  const { groupIds } = action.payload;
  const config = yield select( state =>  state.config);
  const fetchResults = yield fetchOrgGroupApi(config.urlApi, groupIds);
  if (fetchResults && !fetchResults.error ) {
    const data = get(fetchResults, 'data', []);
    yield put(Actions.setOrgGroups([...data]));
  } else {
    const errorToast = {
      message: 'Load org groups failed',
      action: 'Dismiss',
      type: constants.FAILED
    };
    yield put(Actions.setLoadGroupsError());
    yield put(showToast(errorToast));
  }
}

function* updateGroup(action) {
  const { groupInfo } = action.payload;
  const config = yield select( state =>  state.config);
  const fetchResults = yield updateOrgGroupApi(config.urlApi, groupInfo);
  if (fetchResults && !fetchResults.error) {
      const state = yield select(state => state[Actions.namespace]);
      const allGroups = [...get(state, 'orgGroups.data', [])];
      const { id } = groupInfo;
      const groupIndex = findIndex(allGroups, { id });
      if (groupIndex > -1) {
        allGroups[groupIndex] = {...groupInfo};
      }
      yield put(Actions.setOrgGroups(allGroups));
  } else {
    const errorToast = {
      message: 'Update group failed',
      action: 'Dismiss',
      type: constants.FAILED
    };
    yield put(showToast(errorToast));
  }
}

function* createGroup(action) {
  const { groupInfo } = action.payload;
  const config = yield select( state =>  state.config);
  const fetchResults = yield createOrgGroupApi(config.urlApi, groupInfo);
  if (fetchResults && !fetchResults.error) {
    const newOrgGroup = get(fetchResults, 'data');
    if (newOrgGroup) {
      const state = yield select(state => state[Actions.namespace]);
      const allGroups = [...get(state, 'orgGroups.data', [])];
      const { id } = newOrgGroup;
      const groupIndex = findIndex(allGroups, { id });
      if (groupIndex < 0) {
        allGroups.push({...newOrgGroup});
      }
      yield put(Actions.setOrgGroups(allGroups));
    }
  } else {
    const errorToast = {
      message: 'Create group failed',
      action: 'Dismiss',
      type: constants.FAILED
    };
    yield put(showToast(errorToast));
  }
}

function* deleteGroup(action) {
  const { id } = action.payload;
  const config = yield select( state =>  state.config);
  const state = yield select(state => state[Actions.namespace]);
  const allGroups = [...get(state, 'orgGroups.data', [])];
  const groupIndex = findIndex(allGroups, { id });
  if (groupIndex > -1) {
    allGroups.splice(groupIndex, 1);
    yield put(Actions.setOrgGroups(allGroups));
  }
  const fetchResults = yield deleteOrgGroupApi(config.urlApi, id);
  if (fetchResults && fetchResults.error) {
    const errorToast = {
      message: 'Unable to delete group',
      action: 'Dismiss',
      type: constants.FAILED
    };
    yield put(showToast(errorToast));
  }
}

function* loginOrgGroup(action) {
  const { id } = action.payload;
  const config = yield select( state =>  state.config);
  const fetchResults = yield loginOrgGroupApi(config.urlApi, id);

  if (fetchResults && fetchResults.code == FETCH_SUCCESS) {
    location.href = '/';
  } else {
    const errorToast = {
      message: 'Switch org failed',
      action: 'Dismiss',
      type: constants.FAILED
    };
    yield put(showToast(errorToast));
  }
}

function* switchApp(action) {
  const { id } = action.payload;
  const { config, roles } = yield select( state => ({config: state.config, roles: state.app.roles}) );

  const allAccessRole = find(roles, {id: constants.ATTRIBUTE_ALL_ACCESS});
  if (allAccessRole) {    //Only try to rollback to the original org if the user has all access role
    const fetchResults = yield rollbackOrgApi(config.urlApi);
    if (fetchResults && !fetchResults.error) {
      window.open(`${config.switchAppRoute}/${id}`);
      const isRollbacked = get(fetchResults, 'rollbacked');
      if (isRollbacked) {
        //Redirect to home since the current url (ex: dashboard) can be outdated 
        const locationParts = window.location;
        const homeUrl = `${locationParts.protocol}//${locationParts.host}`;
        window.location.replace(homeUrl);
      }
    }
  } else {
    window.open(`${config.switchAppRoute}/${id}`);
  }
}

function* loadEnableEngines() {
  const state = yield select();
  const config = state.config;
  const userState = state.user;
  const playoutEngineId = get(config, 'playoutEngine.engineId');
  const attributeEngineId = get(config, 'attributeEngine.engineId');
  const orgId = get(userState, 'user.organization.organizationId');
  const engines = [attributeEngineId, playoutEngineId];
  const enabledEngines = {};
  
  const recordsResult = yield fetchAttributionEngines(config.urlApi, engines);
  if (recordsResult && !recordsResult.error) {
    const unPublicEngines = recordsResult.filter(engine => {
      if (engine.isPublic) {
        Object.assign(enabledEngines, {
          [engine.id]: true
        })
      };
      
      return !engine.isPublic
    });

    if (unPublicEngines.length > 0) {
      const whitelistResults = yield fetchOrgWhitelistByOrgId(config.urlApi, orgId);
      if (whitelistResults && !whitelistResults.error) {
        const whitelist = get(whitelistResults, 'whitelist.engines', []);
        
        unPublicEngines.forEach(engine => {
          if (find(whitelist, { id: engine.id })) {
            Object.assign(enabledEngines, {
              [engine.id]: true
            })
          }
        })
      } else {
        const errorToast = {
          message: 'Get whitelist failed',
          action: 'Dismiss',
          type: constants.FAILED
        };
        yield put(showToast(errorToast));
      }
    }
    
  } else {
    const errorToast = {
      message: 'Get attribution engines failed',
      action: 'Dismiss',
      type: constants.FAILED
    };
    yield put(showToast(errorToast));
  }
  yield put(Actions.setEnableEngines(enabledEngines));
}

export default function* orgManagement() {
  const ActionType = Actions.ActionType;

  yield all([
    takeEvery(ActionType.LOAD_ORGS, loadAllOrgs),
    takeEvery(ActionType.LOAD_ORGS_BY_IDS, loadOrgsByIds),
    takeEvery(ActionType.LOAD_ORG_GROUPS, loadOrgGroups),
    takeEvery(ActionType.UPDATE_ORG_GROUP, updateGroup),
    takeEvery(ActionType.CREATE_ORG_GROUP, createGroup),
    takeEvery(ActionType.DELETE_ORG_GROUP, deleteGroup),
    takeEvery(ActionType.LOGIN_ORG_GROUP, loginOrgGroup),
    takeEvery(ActionType.SWITCH_APP, switchApp),
    takeEvery(ActionType.LOAD_ACCESSIBLE_ORGS, loadAccessibleOrgs),
    takeEvery(ActionType.LOAD_ENABLE_ENGINES, loadEnableEngines)
  ]);
};
